import {
  ServiceEmailRequestSubmitData,
  ServiceEmailSolicitudDraft,
  ServiceEmailStatus,
} from "../models/ServiceEmailRequestModel";

export type ParseJsonResult =
  | { ok: true; items: ServiceEmailRequestSubmitData[] }
  | { ok: false; error: string };

const normalizeTime = (value: unknown): string | null => {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(raw)) {
    return raw.length === 5 ? `${raw}:00` : raw;
  }
  return raw;
};

const normalizeDateTime = (value: unknown): string | null => {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) return `${raw}:00`;
  return raw;
};

const mapSolicitudes = (
  solicitudesList: unknown[],
  emailObj: Record<string, unknown>
): ServiceEmailSolicitudDraft[] =>
  (solicitudesList.length > 0
    ? solicitudesList
    : [
        {
          passenger_count: emailObj.passenger_count ?? 1,
          passenger_names: "",
          request_date: emailObj.request_date ?? null,
          request_time: emailObj.request_time ?? null,
          origin_text: emailObj.origin_text ?? "",
          destination_text: emailObj.destination_text ?? "",
        },
      ]
  ).map((item) => {
    const row = (item && typeof item === "object" ? item : {}) as Record<string, unknown>;
    return {
      passenger_count: Math.max(1, Number(row.passenger_count) || 1),
      passenger_names: String(row.passenger_names ?? "").trim(),
      request_date: row.request_date ? String(row.request_date) : null,
      request_time: normalizeTime(row.request_time),
      origin_text: String(row.origin_text ?? "").trim(),
      destination_text: String(row.destination_text ?? "").trim(),
    };
  });

const parseOneNested = (
  root: Record<string, unknown>,
  senderRefFallback: string,
  requireSender: boolean
): { ok: true; data: ServiceEmailRequestSubmitData } | { ok: false; error: string } => {
  const emailRaw = root.email;
  if (!emailRaw || typeof emailRaw !== "object" || Array.isArray(emailRaw)) {
    return { ok: false, error: 'Falta el objeto "email".' };
  }

  const emailObj = emailRaw as Record<string, unknown>;
  const subject = String(emailObj.subject ?? "").trim();
  if (!subject) {
    return { ok: false, error: 'El email debe incluir "subject".' };
  }

  const sender_ref = String(emailObj.sender_ref ?? senderRefFallback ?? "").trim();
  if (requireSender && !sender_ref) {
    return { ok: false, error: "Selecciona un emisor (o inclúyelo como sender_ref en el JSON)." };
  }

  const solicitudesRaw = root.solicitudes;
  let solicitudesList: unknown[] = [];
  if (solicitudesRaw == null) {
    solicitudesList = [];
  } else if (Array.isArray(solicitudesRaw)) {
    solicitudesList = solicitudesRaw;
  } else {
    return { ok: false, error: '"solicitudes" debe ser un arreglo.' };
  }

  const solicitudes = mapSolicitudes(solicitudesList, emailObj);
  const first = solicitudes[0];
  const statusRaw = String(emailObj.status ?? "PENDIENTE").toUpperCase();
  const status: ServiceEmailStatus = statusRaw === "AGREGADO" ? "AGREGADO" : "PENDIENTE";

  return {
    ok: true,
    data: {
      email: {
        subject,
        received_at: normalizeDateTime(emailObj.received_at),
        sender_ref,
        has_multiple_requests: Boolean(emailObj.has_multiple_requests) || solicitudes.length > 1,
        request_date: emailObj.request_date
          ? String(emailObj.request_date)
          : first?.request_date ?? null,
        request_time: normalizeTime(emailObj.request_time) ?? first?.request_time ?? null,
        passenger_count: Math.max(
          1,
          Number(emailObj.passenger_count) || first?.passenger_count || 1
        ),
        origin_text: String(emailObj.origin_text ?? first?.origin_text ?? "").trim(),
        destination_text: String(
          emailObj.destination_text ?? first?.destination_text ?? ""
        ).trim(),
        status,
      },
      solicitudes,
    },
  };
};

const splitByRequestDate = (
  data: ServiceEmailRequestSubmitData
): ServiceEmailRequestSubmitData[] => {
  const groups = new Map<string, ServiceEmailSolicitudDraft[]>();
  for (const row of data.solicitudes) {
    const key = row.request_date || data.email.request_date || "__sin_fecha__";
    const list = groups.get(key) ?? [];
    list.push(row);
    groups.set(key, list);
  }

  if (groups.size <= 1) return [data];

  return Array.from(groups.entries()).map(([dayKey, solicitudes]) => {
    const first = solicitudes[0];
    const dayLabel = dayKey === "__sin_fecha__" ? "" : dayKey;
    const baseSubject = data.email.subject;
    const subject =
      dayLabel && !baseSubject.includes(dayLabel)
        ? `${baseSubject} · ${dayLabel}`
        : baseSubject;
    return {
      email: {
        ...data.email,
        subject,
        sender_ref: data.email.sender_ref,
        has_multiple_requests: solicitudes.length > 1,
        request_date: first?.request_date ?? (dayLabel || null),
        request_time: first?.request_time ?? null,
        passenger_count: first?.passenger_count ?? 1,
        origin_text: first?.origin_text ?? "",
        destination_text: first?.destination_text ?? "",
      },
      solicitudes,
    };
  });
};

/**
 * Parsea JSON OPARA:
 * - { email, solicitudes }
 * - { emails: [ { email, solicitudes }, ... ] }
 * - [ { email, solicitudes }, ... ]
 *
 * Si hay un solo correo con solicitudes en días distintos, se separa un correo por día.
 */
export const parseOparaEmailJson = (
  raw: string,
  senderRefFallback = "",
  options: { requireSender?: boolean; splitByDay?: boolean } = {}
): ParseJsonResult => {
  const requireSender = options.requireSender ?? false;
  const splitByDay = options.splitByDay ?? true;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: "JSON inválido. Revisa la sintaxis." };
  }

  const blocks: unknown[] = [];
  if (Array.isArray(parsed)) {
    blocks.push(...parsed);
  } else if (parsed && typeof parsed === "object") {
    const root = parsed as Record<string, unknown>;
    if (Array.isArray(root.emails)) {
      blocks.push(...root.emails);
    } else if (root.email) {
      blocks.push(root);
    } else {
      return {
        ok: false,
        error: "Se esperaba { email, solicitudes }, { emails: [...] } o un arreglo de correos.",
      };
    }
  } else {
    return { ok: false, error: "Se esperaba un objeto o arreglo JSON." };
  }

  if (blocks.length === 0) {
    return { ok: false, error: "No hay correos en el JSON." };
  }

  const items: ServiceEmailRequestSubmitData[] = [];
  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    if (!block || typeof block !== "object" || Array.isArray(block)) {
      return { ok: false, error: `El correo #${index + 1} no es un objeto válido.` };
    }
    const one = parseOneNested(block as Record<string, unknown>, senderRefFallback, requireSender);
    if (!one.ok) {
      return { ok: false, error: `Correo #${index + 1}: ${one.error}` };
    }
    if (splitByDay) {
      items.push(...splitByRequestDate(one.data));
    } else {
      items.push(one.data);
    }
  }

  return { ok: true, items };
};

export const MULTI_DAY_JSON_PLACEHOLDER = `{
  "emails": [
    {
      "email": {
        "subject": "Aviso al proveedor (día 1)",
        "received_at": "2026-07-27T10:59:00",
        "request_date": "2026-07-27",
        "request_time": "12:30:00",
        "origin_text": "Planta Río Loa",
        "destination_text": "Hotel Aguas del Desierto",
        "passenger_count": 1,
        "status": "PENDIENTE"
      },
      "solicitudes": [
        {
          "passenger_count": 1,
          "passenger_names": "NOMBRE, APELLIDO",
          "request_date": "2026-07-27",
          "request_time": "12:30:00",
          "origin_text": "Planta Río Loa",
          "destination_text": "Hotel Aguas del Desierto"
        }
      ]
    },
    {
      "email": {
        "subject": "Aviso al proveedor (día 2)",
        "received_at": "2026-07-27T10:59:00",
        "request_date": "2026-07-28",
        "request_time": "08:00:00",
        "origin_text": "Hotel Aguas del Desierto",
        "destination_text": "Planta Río Loa",
        "passenger_count": 2,
        "status": "PENDIENTE"
      },
      "solicitudes": [
        {
          "passenger_count": 2,
          "request_date": "2026-07-28",
          "request_time": "08:00:00",
          "origin_text": "Hotel Aguas del Desierto",
          "destination_text": "Planta Río Loa"
        }
      ]
    }
  ]
}`;
